import type { ReactNode } from 'react';
import { STRINGS } from '@/strings/strings';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';

/**
 * v3.1 §6 Schritt 1 line 272 · K2 Ein-Satz-Bestätigung.
 *
 * Bau-Spez verbatim: "Ein-Satz-Bestätigung (K2) als Overlay, Dialog
 * `max-w-[520px]`, oben statt mittig ausgerichtet, damit der Sprung
 * von Person zu Person das Auge nicht bewegt. Zugeklappt: Zählzeile,
 * Rundbild `h-24 w-24`, ein Satz `text-lg`, Gold-Knopf, Textlink
 * `Details anpassen`. Aufgeklappt wächst derselbe Dialog, er wechselt
 * nicht."
 *
 * ═══ ROLE IN THE FLOW ═══════════════════════════════════════════════
 *
 * PersonenRoute's block 2 (Weitere Personen) inline-edit flow ended
 * at "Speichern" in Commit 2 — the card became answered immediately.
 * Commit 3 swaps that moment for a K2 confirmation:
 *
 *   1. Customer fills the inline edit form (name, age, face, optional
 *      full-body)
 *   2. Customer clicks the block-level Weiter — the card is complete
 *   3. K2 dialog opens: photo + one-sentence summary + Gold "Passt so"
 *      + "Details anpassen" text link
 *   4. Gold click → `onConfirm` → dialog closes → card is answered
 *   5. Text-link click → `onEditDetails` → K2 closes → edit form
 *      reopens (parent decides how to reopen; K2 does not own that)
 *
 * ═══ EXPANDED STATE — deferred to Phase 6 tuning ═══════════════════
 *
 * The spec's "aufgeklappt wächst derselbe Dialog" is the same dialog
 * growing into a full editor without swapping. For Commit 3 the
 * expanded state is NOT built inside the dialog — the text link exits
 * the dialog and the caller's inline edit form reopens beneath it.
 * The two-mode dialog is a Phase-6 tuning surface once the Bestellung
 * screen tests the pattern with real per-cast content. Tracker
 * `[book-k2-expanded-state-inline]` records it.
 *
 * ═══ TOP-ALIGNED (not centered) ═══════════════════════════════════
 *
 * Radix Dialog's default `DialogContent` centers vertically. K2
 * needs top-alignment so the eye doesn't jump between confirmations
 * as the customer works through the cast. Applied via className
 * override on `top` + `translate-y-0`, negating the default
 * `top-1/2 -translate-y-1/2` from `@/components/ui/dialog:47`.
 */

export interface K2ConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** "3 von 8" or similar — Zählzeile above the photo. */
  readonly zaehlzeile: string;
  /** Blob-URL preview of the face photo. Rundbild. */
  readonly photoUrl: string | null;
  /**
   * The one-sentence summary — `<Name>[, <sex>][, <age> Jahre]` per
   * the 2026-09-18 ruling. Empty string / null means "no line, photo
   * only" (customer opened the dialog before typing name).
   */
  readonly satz: ReactNode;
  readonly onConfirm: () => void;
  /**
   * Sebastian ruling 2026-09-18 — replaces the pre-ruling
   * `onEditDetails` prop. The second button now drops the picked
   * photo and reopens the OS file picker on the same slot; the
   * caller owns both effects.
   */
  readonly onReplacePhoto: () => void;
}

// max-w-[520px] verbatim from §6 line 272. Top-aligned to avoid
// eye-jump between per-cast confirmations.
//
// Sebastian ruling 2026-09-24 (iPhone Safari test) — a portrait
// photo (9:16 screenshot, etc.) filled the dialog height and pushed
// both buttons underneath Safari's bottom toolbar. Root causes:
//   1. `100vh` on iOS Safari includes the area BEHIND the address
//      bar / bottom toolbar, so the "usable" dialog area was
//      smaller than the max-h claimed.
//   2. The image had `h-auto` inside an `overflow-y-auto` middle
//      region — a tall image simply grew the region past the
//      footer and pushed the buttons out.
//   3. There was no bottom safe-area padding, so on devices with
//      a home indicator the buttons hugged the physical edge.
//
// Fix applied here:
//   - Replace `100vh` with `100dvh` (dynamic viewport height —
//     shrinks to the actual visible area when the toolbar is up).
//   - Inner middle region is `flex-1 min-h-0` so its contents can
//     shrink below the intrinsic image height. `min-h-0` is the
//     non-obvious ingredient — without it a flex child refuses to
//     go below its content size.
//   - The image lives inside a `flex-1 min-h-0` wrapper and uses
//     `max-h-full max-w-full object-contain` to scale down to fit
//     the space that remains above the footer, keeping its
//     aspect ratio. It NEVER pushes the footer out.
//   - Footer uses `pb-[max(1.5rem,env(safe-area-inset-bottom))]`
//     so on iPhone the padding grows to clear the home indicator,
//     on desktop it stays at the base 1.5rem.
//   - Outer max-h: `calc(100dvh-5rem)` = 4rem top-16 + 1rem
//     breathing room above the safe-area — the safe-area lives
//     INSIDE the footer padding, not in this outer calc.
const CONTENT_CLASS =
  'flex max-w-[520px] top-16 translate-y-0 max-h-[calc(100dvh-5rem)] flex-col border-0 bg-dream-white p-0 shadow-xl';

const BUTTON_GOLD =
  'h-12 w-full rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60';

export function K2ConfirmDialog({
  open,
  onOpenChange,
  zaehlzeile,
  photoUrl,
  satz,
  onConfirm,
  onReplacePhoto,
}: K2ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={CONTENT_CLASS} data-testid="k2-dialog">
        <DialogTitle className="sr-only">{STRINGS.book.personen.k2DialogTitle}</DialogTitle>
        <DialogDescription className="sr-only">
          {STRINGS.book.personen.k2DialogDescription}
        </DialogDescription>

        {/* Middle region — flex column with `min-h-0` so its
            children can shrink below their intrinsic sizes. The
            image wrapper is `flex-1 min-h-0` and lets the image
            `object-contain` scale into whatever space is left
            after the header text and footer are laid out. Pre-
            2026-09-24 this was `overflow-y-auto` with an `h-auto`
            image, which grew the region past the footer on tall
            portraits and pushed the buttons off-screen in
            Safari. */}
        <div className="flex flex-1 min-h-0 flex-col items-center gap-4 p-6 text-center">
          <p className="text-sm text-dream-navy/70">{zaehlzeile}</p>
          {/* Block 2 commit 1 (Sebastian ruling 2026-08-31 E): photo
              fills the dialog width at its own aspect ratio; dialog
              grows in height. A portrait photo makes a tall dialog;
              that is the moment the customer is supposed to check.
              Pre-Block-2 rendered as `h-24 w-24 rounded-full` — the
              old round crop threw away the recognisable areas of the
              image. Round-2 rev 2026-09-24: image `object-contain`
              scales down to fit the flex-1 area above the footer;
              it never grows past the buttons. */}
          <div className="flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden rounded-md bg-dream-navy/10">
            {photoUrl !== null && (
              <img src={photoUrl} alt="" className="block max-h-full max-w-full object-contain" />
            )}
          </div>
          {satz !== '' && satz !== null && (
            <p className="text-lg font-semibold text-dream-navy">{satz}</p>
          )}
        </div>
        {/* Footer — `shrink-0` so it never gives up its height, and
            `pb-[max(1.5rem,env(safe-area-inset-bottom))]` so on iPhone
            the padding grows to clear the home indicator, on desktop
            it stays at 1.5rem. Both buttons are always in-view no
            matter the photo's aspect ratio. */}
        <div className="shrink-0 flex flex-col gap-3 border-t border-dream-navy/10 bg-dream-white px-6 pt-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          <button type="button" onClick={onConfirm} className={BUTTON_GOLD}>
            {STRINGS.book.personen.k2ConfirmCta}
          </button>
          <button
            type="button"
            onClick={onReplacePhoto}
            className="text-sm text-dream-navy underline focus-visible:outline-2 focus-visible:outline-dream-navy/50"
          >
            {STRINGS.book.personen.k2ReplacePhotoLink}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
